import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useApp } from "../context/AppContext";
import { ProductGrid } from "../components/ProductCard";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

import { tx, useLang } from "../lib/i18n";
const Chip = ({ active, onClick, children, testid }) => { useLang(); return (
  <button onClick={onClick} data-testid={testid}
    className={`rounded-full border px-3.5 py-1.5 text-xs transition-colors ${active ? "border-[var(--mv-teal)] bg-[var(--mv-teal-dim)] text-[var(--mv-teal)]" : "border-[var(--mv-border-2)] text-[var(--mv-muted)] hover:text-[var(--mv-text)]"}`}>
    {children}
  </button>
); };

export const PageHead = ({ eyebrow, title, children }) => { useLang(); return (
  <div className="pt-12 pb-8">
    {tx(eyebrow && <p className="text-xs font-semibold tracking-[0.16em] text-[var(--mv-teal)]">{tx(eyebrow)}</p>)}
    <h1 className="mt-3 text-4xl sm:text-5xl font-semibold tracking-tight">{title}</h1>
    {children}
  </div>
); };

export default function Shop() { const lang = useLang();
  const { t } = useApp();
  const [sp, setSp] = useSearchParams();
  const [cats, setCats] = useState([]);
  const [brands, setBrands] = useState([]);
  const [res, setRes] = useState(null);
  const q = sp.get("q") || "";
  const category = sp.get("category") || "";
  const brand = sp.get("brand") || "";
  const sort = sp.get("sort") || "newest";

  useEffect(() => { api.get("/categories").then((r) => setCats(r.data)); api.get("/brands").then((r) => setBrands(r.data)); }, [lang]);
  useEffect(() => {
    setRes(null);
    api.get("/products", { params: { q: q || undefined, category: category || undefined, brand: brand || undefined, sort, limit: 60 } }).then((r) => setRes(r.data));
  }, [q, category, brand, sort, lang]);

  const set = (k, v) => { const n = new URLSearchParams(sp); v ? n.set(k, v) : n.delete(k); setSp(n); };

  return (
    <div className="mv-container" data-testid="shop-page">
      <PageHead eyebrow="MIVO" title={q ? `“${q}”` : (cats.find((c) => c.slug === category)?.display_name || t("shop.title"))}>
        <p className="mt-3 text-sm text-[var(--mv-muted)]" data-testid="shop-result-count">{tx(res ? `${res.total} ${t("common.results")}` : t("common.loading"))}</p>
      </PageHead>
      <div className="mb-8 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="space-y-3">
          <div className="flex flex-wrap gap-2" data-testid="category-filters">
            <Chip active={!category} onClick={() => set("category", "")} testid="category-filter-all">{t("shop.all")}</Chip>
            {cats.filter((c) => !c.parent_id && c.count > 0).map((c) => <Chip key={c.slug} active={category === c.slug} onClick={() => set("category", c.slug)} testid={`category-filter-${c.slug}`}>{c.display_name || c.name} · {c.count}</Chip>)}
            {(() => { const cur = cats.find((c) => c.slug === category); const pid = cur ? (cur.parent_id || cur.category_id) : null;
              return cats.filter((c) => pid && c.parent_id === pid && c.count > 0).map((c) => <Chip key={c.slug} active={category === c.slug} onClick={() => set("category", c.slug)} testid={`subcategory-filter-${c.slug}`}>↳ {c.display_name || c.name} · {c.count}</Chip>); })()}
          </div>
          <div className="flex flex-wrap gap-2" data-testid="brand-filters">
            {brands.filter((b) => b.count > 0).map((b) => <Chip key={b.slug} active={brand === b.slug} onClick={() => set("brand", brand === b.slug ? "" : b.slug)} testid={`brand-filter-${b.slug}`}>{b.name} · {tx(b.count)}</Chip>)}
          </div>
        </div>
        <Select value={sort} onValueChange={(v) => set("sort", v)}>
          <SelectTrigger className="w-[200px] bg-[var(--mv-card)] border-[var(--mv-border-2)]" data-testid="shop-sort-select"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[var(--mv-card)] border-[var(--mv-border-2)] text-[var(--mv-text)]">
            <SelectItem value="newest">{t("shop.sort.newest")}</SelectItem>
            <SelectItem value="price_asc">{t("shop.sort.priceAsc")}</SelectItem>
            <SelectItem value="price_desc">{t("shop.sort.priceDesc")}</SelectItem>
            <SelectItem value="name">{t("shop.sort.name")}</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {tx(res && res.items.length === 0 && (
        <div className="rounded-2xl border border-dashed border-[var(--mv-border-2)] p-12 text-center" data-testid="shop-empty">
          <p className="text-[var(--mv-muted)]">{t("common.noResults")}</p>
          <Link to="/shop" className="mt-4 inline-block text-sm text-[var(--mv-teal)]">{t("shop.all")}</Link>
        </div>
      ))}
      {tx(res && <ProductGrid items={res.items} />)}
    </div>
  );
}
